import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  AlertCircle,
  Compass,
  Filter,
  Loader2,
  Search,
  SearchX,
  Settings2,
  X,
} from "lucide-react";

import {
  DISCOVER_QUICK_TOPICS,
  discoverHeroClass,
  discoverSectionHeaderClass,
} from "@/components/grant_suite/grantDiscoverUi";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type DiscoverHeroProps = {
  query: string;
  onQueryChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  loading: boolean;
  canSubmit: boolean;
  profileComplete: boolean;
  compact?: boolean;
};

export function GrantDiscoverHero({
  query,
  onQueryChange,
  onSubmit,
  loading,
  canSubmit,
  profileComplete,
  compact = false,
}: DiscoverHeroProps) {
  return (
    <div className={discoverHeroClass}>
      {!compact ? (
        <>
          <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-emerald-400/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-10 -left-6 h-36 w-36 rounded-full bg-sky-400/10 blur-3xl" />
        </>
      ) : null}

      <div className="relative space-y-3">
        {!compact ? (
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Compass className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1 space-y-0.5">
              <h2 className="text-lg font-semibold tracking-tight sm:text-xl">
                Find funding for your community
              </h2>
              <p className="max-w-2xl text-sm text-muted-foreground">
                Search federal and regional programs in plain language.
                {profileComplete
                  ? " Results are scored against your affinity profile."
                  : " Set up your profile to unlock fit scores and recommendations."}
              </p>
            </div>
          </div>
        ) : (
          <p className="text-sm font-medium text-muted-foreground">Search query</p>
        )}

        <form onSubmit={onSubmit} className="space-y-3">
          <div className="rounded-lg border bg-background/80 p-3 shadow-sm backdrop-blur-sm sm:p-4">
            {!compact ? (
              <div className="space-y-1">
                <label className="text-sm font-medium" htmlFor="grant-query-discover">
                  What funding are you looking for?
                </label>
                <p className="text-xs text-muted-foreground">
                  Describe goals in plain language. We match by meaning and also check titles and program names.
                </p>
              </div>
            ) : null}
            <Textarea
              id="grant-query-discover"
              placeholder="Describe the funding you need…"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSubmit(e as unknown as React.FormEvent);
                }
              }}
              className={cn(
                "min-h-[72px] resize-none border-0 bg-transparent px-0 shadow-none focus-visible:ring-0",
                !compact && "mt-2",
              )}
              rows={compact ? 2 : 3}
            />
            <div className="mt-2 flex justify-end border-t border-border/50 pt-2">
              <Button type="submit" size="sm" disabled={loading || !canSubmit} className="shrink-0">
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="mr-2 h-4 w-4" />
                )}
                Search catalog
              </Button>
            </div>
          </div>

          {!compact ? (
            <div className="flex items-center gap-2 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <span className="shrink-0 text-xs text-muted-foreground">Try:</span>
              {DISCOVER_QUICK_TOPICS.map((topic) => (
                <Button
                  key={topic}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 shrink-0 rounded-full bg-background/70 px-3 text-xs font-normal"
                  onClick={() => onQueryChange(topic)}
                >
                  {topic.split(" ").slice(0, 3).join(" ")}…
                </Button>
              ))}
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
}

type StatusBanner = {
  id: string;
  tone: "info" | "warning" | "error" | "loading";
  message: ReactNode;
  action?: { label: string; onClick: () => void };
};

export function GrantDiscoverStatusStack({ banners }: { banners: StatusBanner[] }) {
  if (banners.length === 0) return null;

  const toneClass: Record<StatusBanner["tone"], string> = {
    info: "border-sky-500/30 bg-sky-50/60 dark:bg-sky-950/20",
    warning: "border-amber-500/30 bg-amber-50/60 dark:bg-amber-950/20",
    error: "border-destructive/30 bg-destructive/5",
    loading: "border-border bg-muted/30",
  };

  return (
    <div className="space-y-2">
      {banners.map((banner) => (
        <div
          key={banner.id}
          className={cn(
            "flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm",
            toneClass[banner.tone],
          )}
        >
          <div className="flex min-w-0 items-start gap-2 text-muted-foreground">
            {banner.tone === "error" ? (
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            ) : banner.tone === "loading" ? (
              <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            )}
            <span className="text-foreground">{banner.message}</span>
          </div>
          {banner.action ? (
            <Button size="sm" variant="outline" onClick={banner.action.onClick}>
              {banner.action.label}
            </Button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function GrantDiscoverInsightsCallout({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <div className="rounded-xl border border-emerald-500/20 bg-emerald-50/40 px-4 py-3 text-sm text-muted-foreground dark:bg-emerald-950/20">
      {children}
    </div>
  );
}

type SectionHeaderProps = {
  title: string;
  description?: string;
  icon?: LucideIcon;
  count?: number;
  countLabel?: string;
};

export function GrantDiscoverSectionHeader({
  title,
  description,
  icon: Icon,
  count,
  countLabel = "programs",
}: SectionHeaderProps) {
  return (
    <div className={discoverSectionHeaderClass}>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="h-4 w-4 text-primary" /> : null}
          <h3 className="text-base font-semibold tracking-tight">{title}</h3>
          {typeof count === "number" ? (
            <Badge variant="secondary" className="font-normal tabular-nums">
              {count} {countLabel}
            </Badge>
          ) : null}
        </div>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
    </div>
  );
}

type FilterChip = { label: string; onRemove: () => void };

type FiltersBarProps = {
  keyword: string;
  onKeywordChange: (value: string) => void;
  onSearchSubmit: (event: React.FormEvent) => void;
  facetsLoading: boolean;
  facets?: { agency?: string[]; state?: string[] };
  agency: string;
  onAgencyChange: (value: string) => void;
  catalogScope: string;
  onCatalogScopeChange: (value: string) => void;
  stateFilter: string;
  onStateFilterChange: (value: string) => void;
  excludeFormula: boolean;
  onExcludeFormulaChange: (value: boolean) => void;
  sort: string;
  onSortChange: (value: string) => void;
  sortOptions: readonly { value: string; label: string }[];
  hasActiveFilters: boolean;
  onClearFilters: () => void;
};

export function GrantDiscoverFiltersBar({
  keyword,
  onKeywordChange,
  onSearchSubmit,
  facetsLoading,
  facets,
  agency,
  onAgencyChange,
  catalogScope,
  onCatalogScopeChange,
  stateFilter,
  onStateFilterChange,
  excludeFormula,
  onExcludeFormulaChange,
  sort,
  onSortChange,
  sortOptions,
  hasActiveFilters,
  onClearFilters,
}: FiltersBarProps) {
  return (
    <div className="sticky top-0 z-10 -mx-1 mb-3 rounded-lg border bg-background/95 px-3 py-2.5 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <Filter className="h-3.5 w-3.5" />
          Refine
        </span>
        <Input
          id="grant-keyword"
          placeholder="Narrow: program #, agency, CPRIT…"
          value={keyword}
          onChange={(e) => onKeywordChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSearchSubmit(e as unknown as React.FormEvent);
          }}
          className="h-9 w-44 text-sm sm:w-52"
        />
        <div className="flex h-9 items-center gap-2 rounded-md border px-2.5">
          <Checkbox
            id="hide-formula-based"
            checked={excludeFormula}
            onCheckedChange={(checked) => onExcludeFormulaChange(checked === true)}
          />
          <Label htmlFor="hide-formula-based" className="cursor-pointer text-sm font-normal">
            Hide formula-based
          </Label>
        </div>
        {!facetsLoading ? (
          <>
            {facets?.agency?.length ? (
              <Select value={agency || "__all__"} onValueChange={(v) => onAgencyChange(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9 w-36 text-sm">
                  <SelectValue placeholder="Agency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Any agency</SelectItem>
                  {facets.agency.map((a) => (
                    <SelectItem key={a} value={a}>
                      {a}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <Select
              value={catalogScope || "__all__"}
              onValueChange={(v) => onCatalogScopeChange(v === "__all__" ? "" : v)}
            >
              <SelectTrigger className="h-9 w-32 text-sm">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All sources</SelectItem>
                <SelectItem value="federal">Federal</SelectItem>
                <SelectItem value="regional">Regional</SelectItem>
              </SelectContent>
            </Select>
            {facets?.state?.length ? (
              <Select
                value={stateFilter || "__all__"}
                onValueChange={(v) => onStateFilterChange(v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="h-9 w-28 text-sm">
                  <SelectValue placeholder="State" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Any state</SelectItem>
                  {facets.state.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <Select value={sort} onValueChange={onSortChange}>
              <SelectTrigger className="h-9 w-40 text-sm">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        ) : null}
        {hasActiveFilters ? (
          <Button variant="ghost" size="sm" className="h-9 text-muted-foreground" onClick={onClearFilters}>
            Clear all
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function GrantDiscoverActiveFilters({
  profileScored,
  chips,
}: {
  profileScored: boolean;
  chips: FilterChip[];
}) {
  if (!profileScored && chips.length === 0) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      {profileScored ? (
        // ENG-1692: one canonical explainer for the fit tiers, here instead of
        // on every card's badge — per-card tooltip machinery is expensive.
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                tabIndex={0}
                className="cursor-help font-normal text-emerald-700 dark:text-emerald-300"
              >
                Profile scored
              </Badge>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent className="max-w-xs">
                Fit tiers (Strong match / Possible fit / Low fit) compare each grant's mission and
                eligibility against your Discover profile. Grants similar to ones you already
                track get a small boost.
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
      ) : null}
      {chips.map((chip) => (
        <Badge
          key={chip.label}
          variant="secondary"
          className="cursor-pointer gap-1 py-1 pr-1.5 font-normal transition-colors hover:bg-secondary/80"
          onClick={chip.onRemove}
        >
          {chip.label}
          <X className="h-3 w-3" />
        </Badge>
      ))}
    </div>
  );
}

export function GrantDiscoverCardSkeletonGrid({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="overflow-hidden">
          <CardHeader className="space-y-3 p-5 pb-2">
            <div className="flex gap-2">
              <Skeleton className="h-5 w-24 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <Skeleton className="h-5 w-4/5" />
          </CardHeader>
          <CardContent className="space-y-2 p-5 pt-0">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="mt-4 h-8 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function GrantDiscoverEmptyResults({
  onNewSearch,
  onClearFilters,
  hasActiveFilters,
  homeState,
}: {
  onNewSearch: () => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  homeState?: string | null;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed bg-muted/20 px-6 py-16 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
        <SearchX className="h-7 w-7 text-muted-foreground/70" />
      </div>
      <p className="text-base font-medium">No programs match your search</p>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">
        Try broader keywords, remove filters, or start a new search with different funding goals.
        {homeState ? ` You can also set Source to Regional and State to ${homeState}.` : ""}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        {hasActiveFilters ? (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            Clear filters
          </Button>
        ) : null}
        <Button size="sm" onClick={onNewSearch}>
          New search
        </Button>
      </div>
    </div>
  );
}

export function GrantDiscoverRecommendationsEmpty({
  profileAwaitingEmbed,
}: {
  profileAwaitingEmbed: boolean;
}) {
  return (
    <Alert>
      <AlertDescription>
        {profileAwaitingEmbed
          ? "Personalized recommendations will appear once your profile finishes building."
          : "No recommendations yet — try searching above or refine your affinity profile."}
      </AlertDescription>
    </Alert>
  );
}

export function GrantDiscoverProfileSetupBanner({ onSetupProfile }: { onSetupProfile: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-primary/30 bg-primary/5 px-4 py-3">
      <div className="space-y-0.5">
        <p className="text-sm font-medium">Unlock fit scores and recommendations</p>
        <p className="text-xs text-muted-foreground">
          Tell us about your municipality&apos;s priorities to rank programs automatically.
        </p>
      </div>
      <Button size="sm" onClick={onSetupProfile}>
        <Settings2 className="mr-2 h-4 w-4" />
        Set up profile
      </Button>
    </div>
  );
}

export function GrantDiscoverResultsGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">{children}</div>;
}
